import axios from 'axios'
import { v4 as uuid } from 'uuid'
import { SparkUser, UserProfile } from 'types'
import { IS_IMPERSONATING_KEY } from './impersonationStorage'

type ProfileResponse = Record<string, any> & {
  userProfileId: string
  partnerId?: number | null
  outOfOfficeAttributes?: {
    oooMessage?: string
    oooFromDate?: string | null
    oooToDate?: string | null
    userOutOfOffice?: boolean
  }
}

export const adaptImpersonatedUser = (
  user: ProfileResponse,
  accessToken = user.accessToken ?? '',
): SparkUser => {
  const outOfOfficeAttributes: SparkUser['outOfOfficeAttributes'] =
    user.outOfOfficeAttributes
      ? {
          oooMessage: user.outOfOfficeAttributes.oooMessage,
          oooFromDate: user.outOfOfficeAttributes.oooFromDate
            ? new Date(user.outOfOfficeAttributes.oooFromDate)
                .toISOString()
                .slice(0, 10)
            : null,
          oooToDate: user.outOfOfficeAttributes.oooToDate
            ? new Date(user.outOfOfficeAttributes.oooToDate)
                .toISOString()
                .slice(0, 10)
            : null,
          userOutOfOffice: user.outOfOfficeAttributes.userOutOfOffice,
        }
      : ({} as Record<string, never>)

  return {
    ...({ ...user, accessToken } as unknown as UserProfile),
    partnerId: user.partnerId ? String(user.partnerId) : '0',
    outOfOfficeAttributes,
  }
}

const createHeaders = (accessToken: string, email: string) => ({
  Authorization: `Bearer ${accessToken}`,
  'X-Api-Id': uuid(),
  'X-username': email,
  'Cache-Control': 'no-cache',
  // Pragma: no-cache is a legacy request header that asks older HTTP/1.0 caches not to serve a cached response.
  Pragma: 'no-cache',
})

export const impersonateUser = async ({
  userProfileId,
  accessToken,
  email,
}: {
  userProfileId: string
  accessToken: string
  email: string
}) => {
  const headers = createHeaders(accessToken, email)
  await axios.post('/api/security/userProfile/v1/clear-cookies', null, {
    headers,
    withCredentials: true,
  })
  const { data } = await axios.post<ProfileResponse>(
    '/api/security/userProfile/v1/impersonate',
    null,
    {
      params: { userId: userProfileId },
      headers,
      withCredentials: true,
    },
  )
  if (!data.userProfileId) {
    throw new Error('The security service did not return a user profile ID')
  }
  return adaptImpersonatedUser(data, accessToken)
}

export const getImpersonationErrorMessage = (error: unknown) => {
  const response = (error as { response?: { status?: number; data?: unknown } })
    ?.response
  if (
    response?.status === 404 &&
    typeof response.data === 'string' &&
    response.data.trim()
  ) {
    return response.data
  }

  return 'Please try again or contact support.'
}

export const restoreOriginalUser = async ({
  accessToken,
  email,
  authDomain,
}: {
  accessToken: string
  email: string
  authDomain: 'ENTERPRISE' | 'POL'
}) => {
  const headers = createHeaders(accessToken, email)
  await axios.post('/api/security/userProfile/v1/clear-cookies', null, {
    headers,
    withCredentials: true,
  })
  const { data } = await axios.get<ProfileResponse>(
    `/api/security/userProfile/login`,
    {
      params: { redirectUri: window.location.origin, authDomain },
      headers,
      withCredentials: true,
    },
  )
  if (!data.userProfileId) {
    throw new Error('The security service did not restore the original profile')
  }
  return adaptImpersonatedUser(data)
}

export const applyImpersonatedUser = (
  user: SparkUser,
  setSparkUser: (user: SparkUser) => void,
) => {
  localStorage.setItem(IS_IMPERSONATING_KEY, 'true')
  localStorage.setItem('userLoginState', JSON.stringify(user))
  setSparkUser(user)
  window.location.reload()
}

export const applyOriginalUser = (
  user: SparkUser,
  setSparkUser: (user: SparkUser) => void,
) => {
  localStorage.removeItem(IS_IMPERSONATING_KEY)
  localStorage.setItem('userLoginState', JSON.stringify(user))
  setSparkUser(user)
  window.location.reload()
}
